import { useState, useCallback } from "react";

export type ResearchAccessMode = "admin" | "member";

const STORAGE_KEY = "research-access-mode";

function getStoredMode(): ResearchAccessMode {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY);
    if (v === "member") return "member";
  } catch {}
  return "admin";
}

/**
 * Shared hook for admin users to toggle between "admin" and "member" view
 * across Alpha with AI Club pages. Persists in sessionStorage so it survives
 * page navigations (including Cashfree redirect-back).
 *
 * Non-admin users always get mode = "member" and cannot toggle.
 */
export function useResearchAccessMode(isAdmin: boolean) {
  const [mode, setModeState] = useState<ResearchAccessMode>(
    isAdmin ? getStoredMode() : "member"
  );

  const setMode = useCallback(
    (next: ResearchAccessMode) => {
      if (!isAdmin) return; // only admins may toggle
      sessionStorage.setItem(STORAGE_KEY, next);
      setModeState(next);
    },
    [isAdmin]
  );

  const toggle = useCallback(() => {
    setMode(mode === "admin" ? "member" : "admin");
  }, [mode, setMode]);

  // Effective booleans for downstream components
  const effectiveIsAdmin = isAdmin && mode === "admin";

  return { mode, setMode, toggle, effectiveIsAdmin };
}
